import { Modal } from "@cd2/components/build/es6"
import { IComponentProps } from "@cd2/tools"
import React, { useContext, useRef, useState } from "react"

export const ModalContext = React.createContext<IModalContext | null>(null)

type ModalProps<T> = Omit<IComponentProps<typeof Modal>, "render" | "onRequestClose"> & {
  render: (args: T) => React.ReactNode
  onRequestClose?: "default" | (() => void)
}
interface IModalContext {
  closeModal: () => void
  openModal: (data: ModalProps<any>, args: any) => void
}

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [context, setContext] = useState<ModalProps<any> | null>(null)
  const dataRef = useRef<any | null>(null)

  function openModal(data: ModalProps<any>, args: any) {
    dataRef.current = args
    setContext(data)
  }
  function closeModal() {
    setContext(null)
    dataRef.current = null
  }

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {context && (
        <Modal
          {...context}
          render={() => context?.render(dataRef.current)}
          onRequestClose={
            context.onRequestClose === "default" ? closeModal : context.onRequestClose
          }
        />
      )}
      {children}
    </ModalContext.Provider>
  )
}

export function useLiftedModal<T>(data: ModalProps<T>) {
  const value = useContext(ModalContext)

  if (!value) {
    throw new Error("ModalProvider must be used within a ModalContext.")
  }

  function openModal(args: any) {
    value?.openModal(data, args)
  }
  function closeModal() {
    value?.closeModal()
  }
  return { openModal, closeModal }
}
